<template>
  <SectionContainer
      :colors="colors"
      :background-variant="backgroundVariant"
      :style="cssVars"
      class="w-full "
  >
  <div class="w-full py-8 px-6 transition-colors duration-300 "
       :class="calcClass" :style="cssVars">
    <div class="mx-auto max-w-[750px]">
      <!-- Centered Title -->
      <component :is="headingTag" class="content-title text-3xl font-bold text-center mb-8" style="color: var(--theme-text-heading);">
        {{ title }}
      </component>

      <!-- Content with Image on the Right -->
      <div class="relative cursor-pointer">
        <!-- Image (Optional) -->
        <img
            v-if="image.url !== '' && showImage"
            :src="resolveMediaUrl(image.url)"
            v-bind="imgAttrs(resolveMediaUrl(image.url), '(min-width: 768px) 320px, 256px')"
            :alt="imgAlt(resolveMediaUrl(image.url), title)"
            class="w-64 md:w-80 float-right ml-6 mb-6 rounded-lg  object-contain"
            @click="light.openBox()"
            @error="showImage = false"
        />
        <video class="w-full h-auto max-h-[80vh] object-contain rounded-lg mb-4" v-else-if="video?.link !== ''"
               :src="resolveVideo(video)"
               :poster="video.poster"
               loop
               muted
               playsinline
               autoplay
        />
        <!-- Website Studio only: the empty photo, marked where it would float. -->
        <div v-else-if="showPhotoSlot" class="w-64 md:w-80 float-right ml-6 mb-6">
          <StudioPhotoSlot />
        </div>

        <div class="text-lg text-justified leading-relaxed space-y-4 prose"
             style="color: var(--theme-text-body);"
             v-html="compiledMarkdown"></div>

        <div class=" w-full mt-4 text-xl font-semibold text-right" v-if="price">{{ price }}</div>

        <div class="relative w-full mt-10">
          <SharePage v-if="sharable" class="absolute bottom-4 right-4"/>

          <!-- Optional CTA -->
          <WebButton v-if="buttonText" class="mt-6" @clicked="buttonClicked">
            {{ buttonText }}
          </WebButton>
        </div>
      </div>
    </div>
  </div>
  </SectionContainer>
    <LightBox v-if="lightBoxUrl !== ''" ref="light" :image-url="lightBoxUrl" :media-type="mediaType" :poster="posterUrl" frosted/>

</template>

<script setup>
import {useSectionHeadingTag} from "@/composables/useSectionHeadingTag.js";
import {useImageAlt} from "@/composables/useImageAlt.js";
const imgAlt = useImageAlt();
import {imgAttrs} from "@/utils/photoSizes.js";
import {computed, ref} from "vue";
import {contentHtml} from "@/utils/contentHtml.js";
import WebButton from "@/components/WebButton.vue";
import SharePage from "@/components/SharePage.vue";
import {resolveMediaUrl, resolveVideo} from "@/composables/images.js";
import LightBox from "@/components/LightBox.vue";
import {storeToRefs} from "pinia";
import {useColorStore} from "@/classes/useColorStore.js";
import SectionContainer from "@/components/SectionContainer.vue";
import {useRouter} from "vue-router";
import {linkTarget} from "@/utils/buttonTarget.js";
import StudioPhotoSlot from "@/components/StudioPhotoSlot.vue";
import {studioMode} from "@/classes/previewBridge.js";
import {photoSlotEmpty} from "@/utils/studioLogic.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();
const headingTag = useSectionHeadingTag();

const props = defineProps({
  title: {type: String, required: true},
  content: {type: String, required: true},
  image: {type: Object, default: {url: ""}}, // Optional image
  video: {type: Object, default: {link: ""}}, // Optional image
  buttonText: {type: String, default: ""}, // Optional CTA button
  buttonLink: {type: String, default: ""}, // Link for the CTA button
  colors: {type: Object, required: true}, // Brand colors
  backgroundVariant: {type: Number, default: 1}, // Background variant
  sharable: {type: Boolean, default: false}, // Show social media sharing
  forceBlandColors: {type: Boolean, default: true}, // Force bland background
  price: {type: String, required: false},
});

// Studio mode only (always 'off' in SSR and live): mark the empty photo.
const showPhotoSlot = computed(() => studioMode.value !== 'off' && photoSlotEmpty({type: 'content', image: props.image, video: props.video}));

const { theme } = storeToRefs(useColorStore());
const router = useRouter();

// The button goes to a page, a section on this page ("#contact"), or a
// phone/email link, the same rules as the hero and call to action.
const buttonClicked = () => {
  const action = linkTarget(props.buttonLink);
  if (!action) return;
  if (action.kind === "scroll") {
    document.getElementById(action.id)?.scrollIntoView({behavior: "smooth", block: "start"});
  } else if (action.kind === "external") {
    window.location.href = action.href;
  } else {
    router.push(back(action.path));
  }
};
const showImage = ref(true);

const lightBoxUrl = computed(() => {
  if (props.image?.url !== '') {
    return resolveMediaUrl(props.image.url)
  }
  if (props.video?.link !== '') {
    return resolveVideo(props.video);
  }
})

const posterUrl = computed(() => {
  if (props.video?.link !== '') {
    return resolveVideo(props.video.poster);
  }

})

const mediaType = computed(() => {
  if (props.image?.url !== '') {
    return "image"
  }
  if (props.video?.link !== '') {
    return "video"
}});



const light = ref(null)
// Markdown or the editor's HTML -> safe HTML, keeping the editor's alignment.
const compiledMarkdown = computed(() => contentHtml(props.content));

const calcClass = computed(() =>
    props.backgroundVariant === 2 ? `${theme.value === 'glass' ? '-my-4 py-14' : '-my-2 py-10'} border-b-4 border-[var(--color-accent)]` : `${theme.value === 'glass' ? 'shadow-sm' : ''} z-10 my-6 border-b-4 border-[var(--color-accent)]`
);

// Dynamic color variables — values alias the canonical --brand-* custom
// properties (set once on the page root) rather than duplicating the raw color.
const cssVars = computed(() => ({
  "--bg-light": !props.forceBlandColors && props.backgroundVariant === 2 ? "var(--brand-whitest)" : "var(--brand-lightest)",
  "--bg-dark": "var(--brand-darkest)",
  "--color-accent": "var(--brand-accent)",
  "--bg-light-variant-1": "var(--brand-whitest)",
  "--bg-light-variant-2": "var(--brand-lightest)",
  "--bg-light-variant-3": "var(--brand-light)",
}));
</script>

<style scoped>

:deep(h1) {
  font-size: 1.5rem;
  font-weight: bolder;
  margin-bottom: 1rem;
}

:deep(h2) {
  font-size: 1.25rem;
  font-weight: bolder;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

/* The title is the page's H1 when no hero is: it draws exactly as the h2 rule above gives it. */
:deep(h1.content-title) {
  font-size: 1.25rem;
  font-weight: bolder;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

:deep(h3) {
  font-size: 1.125rem;
  font-weight: bolder;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
}

:deep(h4) {
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

:deep(h5) {
  font-size: 1.05rem;
  font-weight: 600;
  margin-top: 0.75rem;
  margin-bottom: 0.25rem;
}

:deep(p) {
  margin-bottom: 1rem;
  line-height: 1.6;
  color: inherit;
}

:deep(ul), :deep(ol) {
  margin-left: 1.5rem;
  margin-bottom: 1rem;
}

:deep(li) {
  margin-bottom: 0.25rem;
}

:deep(ul) {
  list-style-type: disc;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
}

:deep(ol) {
  list-style-type: decimal;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
}

/* Alignment set in the editor. */
:deep(.ql-align-center) {
  text-align: center;
}

:deep(.ql-align-right) {
  text-align: right;
}

:deep(.ql-align-justify) {
  text-align: justify;
}

/* Image Styling for Text Wrapping */
img {
  transition: transform 0.2s ease-in-out;
}

img:hover {
  transform: scale(1.05);
}

/* Clear the Float on Smaller Screens */
@media (max-width: 768px) {
  img {
    float: none;
    margin: 0 auto 1.5rem;
    display: block;
  }
}

.section-container {
  position: relative;
  /*background-color: rgba(255, 255, 255, 0.6);*/
  backdrop-filter: blur(18px);
  border: 1px solid rgba(255,255,255,0.1);
}
</style>
